/**
 * Shop advertising v2 — campaign builder, creative review, CPM/CPC pacing.
 *
 * Backed by:
 *   * shop.ad_placements / ad_campaigns / ad_creatives / ad_budget_ledger
 *     (supabase/migrations/20260601030000_shop_advertising_v2_init.sql)
 *   * analytics.ad_events + rollups + SDFs
 *     (supabase/migrations/20260601030100_analytics_ad_events_init.sql)
 *   * shop.fn_select_ads / fn_recompute_campaign_state
 *     (supabase/migrations/20260601030200_shop_ad_selection_and_pacing.sql)
 *
 * Edge functions:
 *   * shop-ad-checkout-topup    -> Stripe Checkout for prepaying balance
 *   * shop-ad-event             -> impression / click ingest
 *   * shop-ad-conversion        -> follow / msg / cart / purchase
 *   * shop-ad-stripe-webhook    -> credits ledger from Stripe
 */

import { supabase, supabaseEndpoint } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type { Json } from '../generated';
import type { ShopProductType } from './shopTypes';

// =========================================================================
// Type model
// =========================================================================

export type ShopAdCampaignKind = 'store' | 'product';
export type ShopAdPricingModel = 'cpm' | 'cpc';

export type ShopAdCampaignStatus =
  | 'draft'
  | 'pending_review'
  | 'rejected'
  | 'scheduled'
  | 'active'
  | 'paused'
  | 'paused_daily_cap'
  | 'depleted'
  | 'completed'
  | 'archived';

export type ShopAdCreativeStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'archived';

export type ShopAdPlacementKind = 'vendor' | 'product' | 'any';
export type ShopAdSurface = 'shop' | 'public_storefront' | 'marketplace';

export type ShopAdCtaTargetKind = 'vendor_profile' | 'product_detail' | 'external';

export interface ShopAdPlacement {
  code: string;
  name: string;
  description: string | null;
  kind: ShopAdPlacementKind;
  surface: ShopAdSurface;
  max_active_ads: number;
  min_bid_cents_cpm: number;
  min_bid_cents_cpc: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ShopAdCampaign {
  id: string;
  vendor_id: string;
  kind: ShopAdCampaignKind;
  product_id: string | null;
  name: string;
  status: ShopAdCampaignStatus;
  pricing_model: ShopAdPricingModel;
  bid_cents: number;
  daily_budget_cents: number;
  total_budget_cents: number;
  prepaid_balance_cents: number;
  spent_cents: number;
  placement_codes: string[];
  target_country_iso2: string[];
  target_product_type: ShopProductType[];
  schedule_starts_at: string | null;
  schedule_ends_at: string | null;
  reviewed_by_admin_id: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export interface ShopAdCreative {
  id: string;
  campaign_id: string;
  vendor_id: string;
  headline: string;
  tagline: string | null;
  cta_text: string;
  cta_target_kind: ShopAdCtaTargetKind;
  cta_target_url: string | null;
  image_url: string | null;
  image_alt: string | null;
  status: ShopAdCreativeStatus;
  flagged_by_lint: boolean;
  flagged_reason: string | null;
  reviewed_by_admin_id: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export type ShopAdLedgerKind =
  | 'topup'
  | 'impression_charge'
  | 'click_charge'
  | 'refund'
  | 'adjustment';

export interface ShopAdBudgetLedgerRow {
  id: string;
  campaign_id: string;
  kind: ShopAdLedgerKind;
  amount_cents: number;
  balance_after_cents: number;
  reference: Json;
  idempotency_key: string | null;
  created_at: string;
}

/** Row shape returned by `shop.fn_select_ads`. */
export interface ShopAdSelection {
  campaign_id: string;
  creative_id: string;
  vendor_id: string | null;
  product_id: string | null;
  vendor_slug: string | null;
  product_slug: string | null;
  vendor_name: string | null;
  product_name: string | null;
  headline: string;
  tagline: string | null;
  cta_text: string;
  cta_target_kind: ShopAdCtaTargetKind;
  cta_target_url: string | null;
  image_url: string | null;
  bid_cents: number;
  pricing_model: ShopAdPricingModel;
  placement_code: string;
  surface: ShopAdSurface | null;
}

// =========================================================================
// Placements
// =========================================================================

export async function listShopAdPlacements(): Promise<ShopAdPlacement[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_placements')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as ShopAdPlacement[];
}

// =========================================================================
// Campaigns
// =========================================================================

export interface ListShopAdCampaignsInput extends KeysetInput {
  vendorId?: string;
  status?: ShopAdCampaignStatus;
  includeArchived?: boolean;
}

export async function listShopAdCampaigns(
  input: ListShopAdCampaignsInput = {},
): Promise<ListResult<ShopAdCampaign>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .select('*', selectCountOption(input.countMode));
  if (!input.includeArchived) q = q.is('archived_at', null);
  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.status) q = q.eq('status', input.status);
  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);
  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopAdCampaign[];
  return {
    rows,
    nextCursor: buildNextCursor(rows as unknown as Array<Record<string, unknown>>, pageSize, 'updated_at'),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getShopAdCampaign(id: string): Promise<ShopAdCampaign | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopAdCampaign | null;
}

export interface CreateShopAdCampaignInput {
  vendorId: string;
  kind: ShopAdCampaignKind;
  productId?: string | null;
  name: string;
  pricingModel: ShopAdPricingModel;
  bidCents: number;
  dailyBudgetCents: number;
  totalBudgetCents: number;
  placementCodes: string[];
  targetCountryIso2?: string[];
  targetProductType?: ShopProductType[];
  scheduleStartsAt?: string | null;
  scheduleEndsAt?: string | null;
  metadata?: Json;
}

export async function createShopAdCampaign(
  input: CreateShopAdCampaignInput,
): Promise<ShopAdCampaign> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .insert({
      vendor_id: input.vendorId,
      kind: input.kind,
      product_id: input.productId ?? null,
      name: input.name,
      status: 'draft',
      pricing_model: input.pricingModel,
      bid_cents: input.bidCents,
      daily_budget_cents: input.dailyBudgetCents,
      total_budget_cents: input.totalBudgetCents,
      placement_codes: input.placementCodes,
      target_country_iso2: input.targetCountryIso2 ?? [],
      target_product_type: input.targetProductType ?? [],
      schedule_starts_at: input.scheduleStartsAt ?? null,
      schedule_ends_at: input.scheduleEndsAt ?? null,
      metadata: input.metadata ?? {},
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopAdCampaign;
}

export interface UpdateShopAdCampaignInput {
  id: string;
  name?: string;
  bidCents?: number;
  dailyBudgetCents?: number;
  totalBudgetCents?: number;
  placementCodes?: string[];
  targetCountryIso2?: string[];
  targetProductType?: ShopProductType[];
  scheduleStartsAt?: string | null;
  scheduleEndsAt?: string | null;
  status?: ShopAdCampaignStatus;
  metadata?: Json;
}

export async function updateShopAdCampaign(
  input: UpdateShopAdCampaignInput,
): Promise<ShopAdCampaign> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name;
  if (input.bidCents !== undefined) patch.bid_cents = input.bidCents;
  if (input.dailyBudgetCents !== undefined) patch.daily_budget_cents = input.dailyBudgetCents;
  if (input.totalBudgetCents !== undefined) patch.total_budget_cents = input.totalBudgetCents;
  if (input.placementCodes !== undefined) patch.placement_codes = input.placementCodes;
  if (input.targetCountryIso2 !== undefined) patch.target_country_iso2 = input.targetCountryIso2;
  if (input.targetProductType !== undefined) patch.target_product_type = input.targetProductType;
  if (input.scheduleStartsAt !== undefined) patch.schedule_starts_at = input.scheduleStartsAt;
  if (input.scheduleEndsAt !== undefined) patch.schedule_ends_at = input.scheduleEndsAt;
  if (input.status !== undefined) patch.status = input.status;
  if (input.metadata !== undefined) patch.metadata = input.metadata;
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopAdCampaign;
}

export async function archiveShopAdCampaign(id: string): Promise<ShopAdCampaign> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .update({ status: 'archived', archived_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopAdCampaign;
}

export async function submitShopAdCampaignForReview(
  campaignId: string,
): Promise<ShopAdCampaignStatus> {
  // NOTE: supabase-js `.rpc(name, args, opts)` does NOT accept `{ schema }`
  // as the third argument — that param is `{ head, count, get }`. Use
  // `.schema('app_vendor').rpc(...)` to dispatch into a non-public schema.
  // (Bug history: passing `{ schema: 'app_vendor' }` here silently routed
  // to `public.<name>` and produced "Could not find the function
  // public.submit_ad_campaign_for_review(p_campaign_id) in the schema
  // cache" when a vendor tried to submit an ad for review.)
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('submit_ad_campaign_for_review', { p_campaign_id: campaignId });
  if (error) throw error;
  return data as ShopAdCampaignStatus;
}

export async function setShopAdCampaignPaused(
  campaignId: string,
  paused: boolean,
): Promise<ShopAdCampaignStatus> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('set_ad_campaign_paused', {
      p_campaign_id: campaignId,
      p_paused: paused,
    });
  if (error) throw error;
  return data as ShopAdCampaignStatus;
}

// =========================================================================
// Creatives
// =========================================================================

export async function listShopAdCreatives(
  campaignId: string,
): Promise<ShopAdCreative[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_creatives')
    .select('*')
    .eq('campaign_id', campaignId)
    .is('archived_at', null)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as ShopAdCreative[];
}

export interface CreateShopAdCreativeInput {
  campaignId: string;
  vendorId: string;
  headline: string;
  tagline?: string | null;
  ctaText?: string;
  ctaTargetKind: ShopAdCtaTargetKind;
  ctaTargetUrl?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  metadata?: Json;
}

export async function createShopAdCreative(
  input: CreateShopAdCreativeInput,
): Promise<ShopAdCreative> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_creatives')
    .insert({
      campaign_id: input.campaignId,
      vendor_id: input.vendorId,
      headline: input.headline,
      tagline: input.tagline ?? null,
      cta_text: input.ctaText ?? 'Learn more',
      cta_target_kind: input.ctaTargetKind,
      cta_target_url: input.ctaTargetUrl ?? null,
      image_url: input.imageUrl ?? null,
      image_alt: input.imageAlt ?? null,
      status: 'draft',
      metadata: input.metadata ?? {},
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopAdCreative;
}

export interface UpdateShopAdCreativeInput {
  id: string;
  headline?: string;
  tagline?: string | null;
  ctaText?: string;
  ctaTargetKind?: ShopAdCtaTargetKind;
  ctaTargetUrl?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  status?: ShopAdCreativeStatus;
  metadata?: Json;
}

export async function updateShopAdCreative(
  input: UpdateShopAdCreativeInput,
): Promise<ShopAdCreative> {
  const patch: Record<string, unknown> = {};
  if (input.headline !== undefined) patch.headline = input.headline;
  if (input.tagline !== undefined) patch.tagline = input.tagline;
  if (input.ctaText !== undefined) patch.cta_text = input.ctaText;
  if (input.ctaTargetKind !== undefined) patch.cta_target_kind = input.ctaTargetKind;
  if (input.ctaTargetUrl !== undefined) patch.cta_target_url = input.ctaTargetUrl;
  if (input.imageUrl !== undefined) patch.image_url = input.imageUrl;
  if (input.imageAlt !== undefined) patch.image_alt = input.imageAlt;
  if (input.status !== undefined) patch.status = input.status;
  if (input.metadata !== undefined) patch.metadata = input.metadata;
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_creatives')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopAdCreative;
}

export async function archiveShopAdCreative(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('ad_creatives')
    .update({ status: 'archived', archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface CreativeLintResult {
  blocked: boolean;
  block_reasons: string[];
  warn_reasons: string[];
}

export async function previewCreativeLint(text: string): Promise<CreativeLintResult> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('preview_creative_lint', { p_text: text });
  if (error) throw error;
  return (data ?? { blocked: false, block_reasons: [], warn_reasons: [] }) as CreativeLintResult;
}

// =========================================================================
// Budget ledger
// =========================================================================

export async function listShopAdBudgetLedger(
  campaignId: string,
  pageSize = 50,
): Promise<ShopAdBudgetLedgerRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('ad_budget_ledger')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: false })
    .limit(Math.max(1, Math.min(pageSize, 500)));
  if (error) throw error;
  return (data ?? []) as ShopAdBudgetLedgerRow[];
}

// =========================================================================
// Stripe top-up checkout (edge function)
// =========================================================================

export interface StartShopAdTopupInput {
  campaignId: string;
  amountCents: number;
  successUrl: string;
  cancelUrl: string;
}

export async function startShopAdTopup(
  input: StartShopAdTopupInput,
): Promise<{ url: string; session_id: string }> {
  const endpoint = supabaseEndpoint();
  const { data: sess } = await supabase().auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) throw new Error('Sign in to top up.');
  const resp = await fetch(`${endpoint.url}/functions/v1/shop-ad-checkout-topup`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: endpoint.anonKey,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      campaign_id: input.campaignId,
      amount_cents: input.amountCents,
      success_url: input.successUrl,
      cancel_url: input.cancelUrl,
    }),
  });
  const payload = await resp.json();
  if (!resp.ok || !payload.ok) {
    throw new Error(payload.error ?? `Topup failed (${resp.status})`);
  }
  return { url: payload.url, session_id: payload.session_id };
}

// =========================================================================
// Selection (buyer-facing)
// =========================================================================

export interface SelectAdsForPlacementInput {
  placementCode: string;
  countryIso2?: string | null;
  productType?: ShopProductType | null;
  limit?: number;
}

export async function selectAdsForPlacement(
  input: SelectAdsForPlacementInput,
): Promise<ShopAdSelection[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('fn_select_ads', {
      p_placement_code: input.placementCode,
      p_country_iso2: input.countryIso2 ?? null,
      p_product_type: input.productType ?? null,
      p_limit: input.limit ?? 3,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdSelection[];
}

// =========================================================================
// Event ingest (impression / click / conversion)
// =========================================================================

export type ShopAdEventType =
  | 'impression'
  | 'click'
  | 'conversion_follow'
  | 'conversion_message_initiated'
  | 'conversion_cart_add'
  | 'conversion_purchase';

export interface RecordShopAdEventInput {
  eventType: ShopAdEventType;
  campaignId: string;
  creativeId?: string | null;
  placementCode?: string | null;
  surface?: ShopAdSurface | null;
  sessionId: string;
  attributedEventId?: number | null;
  attributedEventTs?: string | null;
  conversionTargetId?: string | null;
  conversionValueCents?: number | null;
}

/**
 * Fire-and-forget event poster. Non-throwing: every failure mode is
 * silently swallowed so the buyer's render path never breaks.
 */
export function recordShopAdEvent(input: RecordShopAdEventInput): void {
  try {
    const endpoint = supabaseEndpoint();
    void fetch(`${endpoint.url}/functions/v1/shop-ad-event`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        apikey: endpoint.anonKey,
      },
      body: JSON.stringify({
        // Per-event id so the server's charge idempotency key identifies
        // THIS event, not a (session, campaign, minute) bucket. Without it
        // a retry that crosses a minute boundary double-charges, and two
        // genuine events in the same minute under-charge.
        event_id: crypto.randomUUID(),
        event_type: input.eventType,
        campaign_id: input.campaignId,
        creative_id: input.creativeId ?? null,
        placement_code: input.placementCode ?? null,
        surface: input.surface ?? null,
        session_id: input.sessionId,
        attributed_event_id: input.attributedEventId ?? null,
        attributed_event_ts: input.attributedEventTs ?? null,
        conversion_target_id: input.conversionTargetId ?? null,
        conversion_value_cents: input.conversionValueCents ?? null,
      }),
    }).catch(() => {});
  } catch {
    /* noop */
  }
}

export type ShopAdConversionTargetKind =
  | 'order'
  | 'follow'
  | 'message_thread'
  | 'cart_add';

export interface RecordShopAdConversionInput {
  eventType: ShopAdEventType;
  campaignId: string;
  creativeId?: string | null;
  sessionId: string;
  attributedEventId?: number | null;
  attributedEventTs?: string | null;
  targetKind: ShopAdConversionTargetKind;
  targetId: string;
  targetValueCents?: number | null;
}

/**
 * Authenticated conversion ping. Stamps the user's JWT so org_id /
 * user_id can land on the analytics row, then writes both the
 * conversion event and the attribution row server-side.
 */
export async function recordShopAdConversion(
  input: RecordShopAdConversionInput,
): Promise<void> {
  try {
    const endpoint = supabaseEndpoint();
    const { data: sess } = await supabase().auth.getSession();
    const accessToken = sess.session?.access_token;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      apikey: endpoint.anonKey,
    };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    await fetch(`${endpoint.url}/functions/v1/shop-ad-conversion`, {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify({
        event_type: input.eventType,
        campaign_id: input.campaignId,
        creative_id: input.creativeId ?? null,
        session_id: input.sessionId,
        attributed_event_id: input.attributedEventId ?? null,
        attributed_event_ts: input.attributedEventTs ?? null,
        target_kind: input.targetKind,
        target_id: input.targetId,
        target_value_cents: input.targetValueCents ?? null,
      }),
    });
  } catch {
    /* noop */
  }
}

// =========================================================================
// Analytics readers (vendor scope)
// =========================================================================

export interface ShopAdCampaignSummary {
  campaign_id: string;
  from: string;
  to: string;
  impressions: number;
  clicks: number;
  ctr: number | null;
  spend_cents: number;
  conversions: number;
  conversion_value_cents: number;
}

export async function getShopAdCampaignSummary(
  campaignId: string,
  fromIso: string,
  toIso: string,
): Promise<ShopAdCampaignSummary> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_ad_campaign_summary', {
      p_campaign_id: campaignId,
      p_from: fromIso,
      p_to: toIso,
    });
  if (error) throw error;
  return data as ShopAdCampaignSummary;
}

export interface ShopAdCampaignTimeseriesRow {
  bucket: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
}

export async function getShopAdCampaignTimeseries(
  campaignId: string,
  fromIso: string,
  toIso: string,
  granularity: 'hour' | 'day' = 'day',
): Promise<ShopAdCampaignTimeseriesRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_ad_campaign_timeseries', {
      p_campaign_id: campaignId,
      p_from: fromIso,
      p_to: toIso,
      p_granularity: granularity,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdCampaignTimeseriesRow[];
}

export interface ShopAdCreativeBreakdownRow {
  creative_id: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
}

export async function getShopAdCreativeBreakdown(
  campaignId: string,
  fromIso: string,
  toIso: string,
): Promise<ShopAdCreativeBreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_ad_creative_breakdown', {
      p_campaign_id: campaignId,
      p_from: fromIso,
      p_to: toIso,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdCreativeBreakdownRow[];
}

export interface ShopAdPlacementBreakdownRow {
  placement_code: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
}

export async function getShopAdPlacementBreakdown(
  campaignId: string,
  fromIso: string,
  toIso: string,
): Promise<ShopAdPlacementBreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_ad_placement_breakdown', {
      p_campaign_id: campaignId,
      p_from: fromIso,
      p_to: toIso,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdPlacementBreakdownRow[];
}

// =========================================================================
// Analytics readers (admin scope)
// =========================================================================

export interface ShopAdAdminOverview {
  from: string;
  to: string;
  active_campaigns: number;
  impressions: number;
  clicks: number;
  ctr: number | null;
  spend_cents: number;
  conversions: number;
  conversion_value_cents: number;
}

export async function getShopAdAdminOverview(
  fromIso: string,
  toIso: string,
): Promise<ShopAdAdminOverview> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_ad_overview', {
      p_from: fromIso,
      p_to: toIso,
    });
  if (error) throw error;
  return data as ShopAdAdminOverview;
}

export interface ShopAdTopCampaignRow {
  campaign_id: string;
  vendor_id: string;
  vendor_slug: string;
  campaign_name: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
}

export async function getTopShopAdCampaigns(
  fromIso: string,
  toIso: string,
  limit = 20,
): Promise<ShopAdTopCampaignRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_top_ad_campaigns', {
      p_from: fromIso,
      p_to: toIso,
      p_limit: limit,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdTopCampaignRow[];
}

export interface ShopAdConversionBreakdownRow {
  target_kind: 'order' | 'follow' | 'message_thread' | 'cart_add';
  conversions: number;
  attributed_value_cents: number;
}

export async function getShopAdConversionBreakdown(
  campaignId: string,
  fromIso: string,
  toIso: string,
): Promise<ShopAdConversionBreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_ad_conversion_breakdown', {
      p_campaign_id: campaignId,
      p_from: fromIso,
      p_to: toIso,
    });
  if (error) throw error;
  return (data ?? []) as ShopAdConversionBreakdownRow[];
}

// =========================================================================
// Admin RPCs (apps/admin)
// =========================================================================

export interface AdminAdCampaign {
  id: string;
  name: string;
  vendor_id: string;
  vendor_slug: string;
  vendor_name: string;
  kind: string;
  status: ShopAdCampaignStatus;
  pricing_model: ShopAdPricingModel;
  bid_cents: number;
  prepaid_balance_cents: number;
  spent_cents: number;
  created_at: string;
}

export async function listAdminAdCampaigns(input: {
  status?: ShopAdCampaignStatus | null;
  limit?: number;
} = {}): Promise<AdminAdCampaign[]> {
  let q = (supabase() as any)
    .schema('shop')
    .from('ad_campaigns')
    .select(
      'id, name, vendor_id, kind, status, pricing_model, bid_cents, prepaid_balance_cents, spent_cents, created_at, vendor:vendor_id(id, slug, company_name)',
    )
    .is('archived_at', null)
    .order('created_at', { ascending: false })
    .limit(input.limit ?? 100);
  if (input.status) q = q.eq('status', input.status);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as any[]).map((r) => ({
    id: r.id,
    name: r.name,
    vendor_id: r.vendor_id,
    vendor_slug: r.vendor?.slug ?? '',
    vendor_name: r.vendor?.company_name ?? '',
    kind: r.kind,
    status: r.status,
    pricing_model: r.pricing_model,
    bid_cents: r.bid_cents,
    prepaid_balance_cents: r.prepaid_balance_cents,
    spent_cents: r.spent_cents,
    created_at: r.created_at,
  })) as AdminAdCampaign[];
}

export interface AdminPendingCreative {
  creative_id: string;
  campaign_id: string;
  campaign_name: string;
  campaign_status: ShopAdCampaignStatus;
  vendor_id: string;
  vendor_slug: string;
  vendor_name: string;
  headline: string;
  tagline: string | null;
  cta_text: string;
  cta_target_kind: ShopAdCtaTargetKind;
  cta_target_url: string | null;
  image_url: string | null;
  flagged_by_lint: boolean;
  flagged_reason: string | null;
  created_at: string;
}

export async function listPendingAdCreatives(): Promise<AdminPendingCreative[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('list_pending_ad_creatives', {});
  if (error) throw error;
  return (data ?? []) as AdminPendingCreative[];
}

export async function countPendingAdCreatives(): Promise<number> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('count_pending_ad_creatives', {});
  if (error) throw error;
  return (data ?? 0) as number;
}

export async function approveAdCreative(creativeId: string, notes?: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('approve_ad_creative', {
      p_creative_id: creativeId,
      p_notes: notes ?? null,
    });
  if (error) throw error;
}

export async function rejectAdCreative(creativeId: string, reason: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('reject_ad_creative', {
      p_creative_id: creativeId,
      p_reason: reason,
    });
  if (error) throw error;
}

export async function forcePauseAdCampaign(campaignId: string, reason: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('force_pause_ad_campaign', {
      p_campaign_id: campaignId,
      p_reason: reason,
    });
  if (error) throw error;
}

export async function resumeAdCampaignAdmin(campaignId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('resume_ad_campaign', { p_campaign_id: campaignId });
  if (error) throw error;
}

export async function refundAdCampaign(
  campaignId: string,
  cents: number,
  reason: string,
): Promise<number> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('refund_ad_campaign', {
      p_campaign_id: campaignId,
      p_cents: cents,
      p_reason: reason,
    });
  if (error) throw error;
  return (data ?? 0) as number;
}

// =========================================================================
// Last-click attribution helper (browser only)
// =========================================================================

const AD_LAST_CLICK_KEY = 'pc_ad_last_click_v1';
const AD_SESSION_KEY = 'pc_ad_session_v1';
const ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface AdLastClickContext {
  event_id: number | null;
  event_ts: string;
  campaign_id: string;
  creative_id: string | null;
  expires_at: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function safeLocalStorage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSessionStorage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function uuid(): string {
  if (isBrowser() && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateAdSessionId(): string {
  const ss = safeSessionStorage();
  if (!ss) return uuid();
  const existing = ss.getItem(AD_SESSION_KEY);
  if (existing && /^[0-9a-fA-F-]{36}$/.test(existing)) return existing;
  const fresh = uuid();
  try {
    ss.setItem(AD_SESSION_KEY, fresh);
  } catch {
    /* ignore */
  }
  return fresh;
}

export function setAdLastClick(ctx: Omit<AdLastClickContext, 'expires_at'>): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    const payload: AdLastClickContext = {
      ...ctx,
      expires_at: Date.now() + ATTRIBUTION_WINDOW_MS,
    };
    ls.setItem(AD_LAST_CLICK_KEY, JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function getAdLastClick(): AdLastClickContext | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(AD_LAST_CLICK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdLastClickContext;
    if (typeof parsed.expires_at !== 'number' || Date.now() > parsed.expires_at) {
      ls.removeItem(AD_LAST_CLICK_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// =========================================================================
// High-level convenience: fireShopAdConversion(...)
//
// Resolves the last-click context from localStorage, the active session
// id from sessionStorage, and forwards to recordShopAdConversion. No-op
// when there is no last-click context or the call site isn't in the
// browser. Non-throwing.
// =========================================================================

export interface FireShopAdConversionInput {
  eventType: ShopAdEventType;
  targetKind: ShopAdConversionTargetKind;
  targetId: string;
  targetValueCents?: number | null;
}

export function fireShopAdConversion(input: FireShopAdConversionInput): void {
  if (!isBrowser()) return;
  const ctx = getAdLastClick();
  if (!ctx) return;
  void recordShopAdConversion({
    eventType: input.eventType,
    campaignId: ctx.campaign_id,
    creativeId: ctx.creative_id ?? null,
    sessionId: getOrCreateAdSessionId(),
    attributedEventId: ctx.event_id ?? null,
    attributedEventTs: ctx.event_ts,
    targetKind: input.targetKind,
    targetId: input.targetId,
    targetValueCents: input.targetValueCents ?? null,
  });
}

export function clearAdLastClick(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.removeItem(AD_LAST_CLICK_KEY);
  } catch {
    /* ignore */
  }
}
